import React from 'react';
import { Box, Typography, Button, Popover, Divider } from '@mui/material';
import { AutoAwesome as SparkleIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../themes/typography';

const AISuggestionPopover = ({ anchorEl, open, onClose, suggestion, onApply }) => {
  const { t } = useTranslation();
  if (!suggestion) return null;

  const isMulti = Array.isArray(suggestion);

  // Multi-slot summary view
  if (isMulti) {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '10px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              maxWidth: 'min(400px, calc(100vw - 24px))',
              overflow: 'visible',
              mt: -1
            }
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <SparkleIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.text.primary }}>
              {t('machines_aiSuggestions', { count: suggestion.length })}
            </Typography>
          </Box>

          <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
            {suggestion.map(({ slot, suggestion: s }, i) => {
              const topCandidate = s?.candidates?.[0];
              return (
                <Box key={`${slot.row}-${slot.column}`} sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.75,
                  borderBottom: i < suggestion.length - 1 ? `1px solid ${colors.border.light}` : 'none'
                }}>
                  <Box sx={{ flex: 1, mr: 1 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: colors.text.primary }}>
                      {slot.selection_id || `${slot.row}-${slot.column}`} — {slot.name}
                    </Typography>
                    {topCandidate && (
                      <Typography sx={{ fontSize: '11px', color: colors.text.secondary }}>
                        → {topCandidate.productName}
                        {topCandidate.improvementPercentage != null && (
                          <Box component="span" sx={{
                            ml: 0.5,
                            fontWeight: 600,
                            color: topCandidate.improvementPercentage >= 0 ? colors.status.success : colors.critical?.text || '#ef4444'
                          }}>
                            {topCandidate.improvementPercentage >= 0 ? '+' : ''}{Math.round(topCandidate.improvementPercentage)}%
                          </Box>
                        )}
                      </Typography>
                    )}
                    {!topCandidate && s?.suggestion && (
                      <Typography sx={{ fontSize: '11px', color: colors.text.muted }}>
                        {s.suggestion}
                      </Typography>
                    )}
                    {!topCandidate && !s?.suggestion && (
                      <Typography sx={{ fontSize: '11px', color: colors.text.muted, fontStyle: 'italic' }}>
                        {t('machines_noAiCandidate')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              size="small"
              onClick={onClose}
              sx={{ textTransform: 'none', fontSize: '12px', color: colors.text.secondary, px: 1.5 }}
            >
              {t('machines_dismiss')}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => onApply(suggestion)}
              sx={{
                textTransform: 'none',
                fontSize: '12px',
                fontWeight: 600,
                px: 2,
                borderRadius: '6px',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' }
              }}
            >
              {t('machines_createExperiments')}
            </Button>
          </Box>
        </Box>
      </Popover>
    );
  }

  // Single-slot view (original)
  const hasReplacementCandidates = suggestion.candidates && suggestion.candidates.length > 0;
  const hasDiscount = suggestion.suggestedDiscount != null;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '10px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            maxWidth: 'min(340px, calc(100vw - 24px))',
            overflow: 'visible',
            mt: -1
          }
        }
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <SparkleIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.text.primary }}>
            {t('machines_aiSuggestion')}
          </Typography>
        </Box>

        {/* Suggestion text */}
        {suggestion.suggestion && (
          <Typography sx={{ fontSize: '12px', color: colors.text.secondary, mb: 1.5, lineHeight: 1.5 }}>
            {suggestion.suggestion}
          </Typography>
        )}

        {/* Discount suggestion */}
        {hasDiscount && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            backgroundColor: 'rgba(59, 130, 246, 0.08)',
            borderRadius: '6px',
            mb: 1.5
          }}>
            <Typography sx={{ fontSize: '12px', color: colors.text.secondary }}>
              {t('machines_suggestedDiscountLabel')}
            </Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.primary.main }}>
              {suggestion.suggestedDiscount}%
            </Typography>
          </Box>
        )}

        {/* Replacement candidates */}
        {hasReplacementCandidates && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: colors.text.muted, mb: 0.75, textTransform: 'uppercase' }}>
              {t('machines_replacementCandidates')}
            </Typography>
            {suggestion.candidates.slice(0, 3).map((candidate, i) => (
              <Box key={i} sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0.5,
                borderBottom: i < Math.min(suggestion.candidates.length, 3) - 1 ? `1px solid ${colors.border.light}` : 'none'
              }}>
                <Typography sx={{ fontSize: '12px', color: colors.text.primary, flex: 1, mr: 1 }}>
                  {candidate.productName}
                </Typography>
                {candidate.improvementPercentage != null && (
                  <Typography sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: candidate.improvementPercentage >= 0 ? colors.status.success : colors.critical?.text || '#ef4444'
                  }}>
                    {candidate.improvementPercentage >= 0 ? '+' : ''}{Math.round(candidate.improvementPercentage)}%
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ mb: 1.5 }} />

        {/* Action buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            size="small"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              color: colors.text.secondary,
              px: 1.5
            }}
          >
            {t('machines_dismiss')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => onApply(suggestion)}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 600,
              px: 2,
              borderRadius: '6px',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' }
            }}
          >
            {t('apply')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default React.memo(AISuggestionPopover);
